/**
 * <khaime-pay-button> Web Component
 * Lightweight payment button that opens checkout modal
 */

import { askForCustomer } from './customer-step';
import { KhaimeBaseElement, BASE_STYLES, ComponentRegistry } from '../base';
import { createKhaimeEvent } from '../../types/events';
import { ADAPTIVE_TOKENS } from '../theme';

const PAY_BUTTON_STYLES = `
  :host {
    ${ADAPTIVE_TOKENS}
    color: inherit;
    opacity: 1 !important;
    font-family: var(--ksp-font-body);
  }

  .ks-pay-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.8125rem 1.5rem;
    font-family: inherit;
    font-size: 0.8125rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    border: none;
    border-radius: var(--ksp-btn-radius);
    cursor: pointer;
    transition: filter 180ms ease, transform 120ms ease, box-shadow 220ms ease;
    min-width: 120px;
  }

  .ks-pay-button:active:not(:disabled) { transform: scale(0.985); }

  .ks-pay-button:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .ks-pay-button--primary {
    background: var(--ksp-accent);
    color: var(--ksp-accent-ink);
  }

  .ks-pay-button--primary:hover:not(:disabled) {
    filter: brightness(1.08);
    box-shadow: 0 6px 18px -6px color-mix(in srgb, var(--ksp-accent) 55%, transparent);
  }

  .ks-pay-button--secondary {
    background: var(--ksp-card-bg-strong);
    color: inherit;
    border: 1px solid var(--ksp-hairline);
  }

  .ks-pay-button--secondary:hover:not(:disabled) {
    border-color: currentColor;
  }

  .ks-pay-button--outline {
    background: transparent;
    color: var(--ksp-accent);
    border: 1.5px solid var(--ksp-accent);
  }

  .ks-pay-button--outline:hover:not(:disabled) {
    background: var(--ksp-accent);
    color: var(--ksp-accent-ink);
  }

  .ks-pay-button--loading { pointer-events: none; }

  .ks-btn-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: ks-spin 0.8s linear infinite;
  }

  @keyframes ks-spin {
    to { transform: rotate(360deg); }
  }
`;

export class KhaimePayButtonElement extends KhaimeBaseElement {
  static tagName = 'khaime-pay-button';

  protected _loading = false;

  static get observedAttributes(): string[] {
    return [
      ...super.observedAttributes,
      'product-id',
      'variant-id',
      'quantity',
      'label',
      'variant',
      'disabled',
    ];
  }

  /**
   * Get product ID
   */
  get productId(): number {
    return parseInt(this.getAttribute('product-id') || '0', 10);
  }

  /**
   * Get variant ID
   */
  get variantId(): number | undefined {
    const val = this.getAttribute('variant-id');
    return val ? parseInt(val, 10) : undefined;
  }

  /**
   * Get quantity
   */
  get quantity(): number {
    return parseInt(this.getAttribute('quantity') || '1', 10);
  }

  /**
   * Get button label
   */
  get label(): string {
    return this.getAttribute('label') || 'Buy Now';
  }

  /**
   * Get button variant style
   */
  get buttonVariant(): 'primary' | 'secondary' | 'outline' {
    const val = this.getAttribute('variant');
    if (val === 'secondary' || val === 'outline') return val;
    return 'primary';
  }

  /**
   * Check if disabled
   */
  get isDisabled(): boolean {
    return this.hasAttribute('disabled');
  }

  /**
   * Override to skip heavy config loading
   */
  protected async loadConfig(): Promise<void> {
    // Apply minimal config for button styling
    this.applyAttributeOverrides();
  }

  /**
   * Render the button
   */
  protected render(): void {
    const variant = this.buttonVariant;
    const disabled = this.isDisabled || this._loading;
    const loadingClass = this._loading ? 'ks-pay-button--loading' : '';

    this.shadow.innerHTML = `
      <style>${BASE_STYLES}${PAY_BUTTON_STYLES}</style>
      <button
        class="ks-pay-button ks-pay-button--${variant} ${loadingClass}"
        part="button"
        ${disabled ? 'disabled' : ''}
      >
        ${this._loading
          ? '<span class="ks-btn-spinner"></span>'
          : `<span class="ks-btn-text">${this.label}</span>`
        }
      </button>
    `;
  }

  /**
   * Set up click handler
   */
  protected setupEventListeners(): void {
    this.shadow.querySelector('button')?.addEventListener('click', () => {
      this.handleClick();
    });
  }

  /**
   * Handle button click
   */
  private async handleClick(): Promise<void> {
    if (this._loading || this.isDisabled) return;

    if (!this.productId) {
      console.error('[Khaime Pay Button] product-id attribute is required');
      return;
    }

    // Name and email first: checkout needs them, and they let the store
    // remind a shopper who leaves before paying.
    const customer = await askForCustomer();
    if (!customer) return;

    this._loading = true;
    this.render();

    try {
      // Initialize checkout session
      if (!this.api) {
        throw new Error('API client not initialized');
      }

      let currency: string | undefined;
      try {
        currency = localStorage.getItem(`khaime:currency:${this.merchantId}`) || undefined;
      } catch {
        currency = undefined;
      }

      const session = await this.api.initCheckout({
        productId: this.productId,
        variantId: this.variantId,
        quantity: this.quantity,
        currency,
        customerEmail: customer.email,
        customerName: customer.name,
      });

      // Emit checkout init event
      this.dispatchEvent(
        createKhaimeEvent('khaime:checkout-init', {
          productId: this.productId,
          amount: session.amount,
          currency: session.currency,
          sessionToken: session.sessionToken,
          merchantId: this.merchantId,
        })
      );

      // Open checkout (lazy load checkout component)
      await this.openCheckout(session.sessionToken);

    } catch (error) {
      console.error('[Khaime Pay Button]', error);
      this.dispatchEvent(
        createKhaimeEvent('khaime:payment-error', {
          error: error instanceof Error ? error.message : 'Checkout failed',
          merchantId: this.merchantId,
        })
      );
    } finally {
      this._loading = false;
      this.render();
      this.setupEventListeners();
    }
  }

  /**
   * Open checkout modal (lazy loads checkout component)
   */
  private async openCheckout(sessionToken: string): Promise<void> {
    // Lazy load checkout component
    const { openCheckoutModal } = await import('./checkout-modal');

    openCheckoutModal({
      merchantId: this.merchantId,
      sessionToken,
      onSuccess: (result) => {
        this.dispatchEvent(
          createKhaimeEvent('khaime:payment-success', {
            sessionToken: result.sessionToken,
            orderId: result.orderId,
            amount: result.amount,
            currency: result.currency,
            paymentMethod: result.paymentMethod,
            merchantId: this.merchantId,
          })
        );
      },
      onError: (error) => {
        this.dispatchEvent(
          createKhaimeEvent('khaime:payment-error', {
            error: error.message,
            sessionToken,
            merchantId: this.merchantId,
          })
        );
      },
      onClose: () => {
        // Modal closed without completing
      },
    });
  }

  /**
   * Public API: Set loading state
   */
  public setLoading(loading: boolean): void {
    this._loading = loading;
    this.render();
    if (!loading) {
      this.setupEventListeners();
    }
  }
}

// Register the component
ComponentRegistry.define('khaime-pay-button', KhaimePayButtonElement);

export default KhaimePayButtonElement;
